import socketService from './socketService';

// Initial admin notifications (used only if localStorage is empty)
const initialAdminNotifications = [
  {
    id: 'admin-notif-2',
    recipientId: 'admin',
    recipientType: 'admin',
    type: 'payment_request',
    title: 'New Payment Request',
    message: 'Test User submitted a payment request for PKR 5,000',
    data: {
      paymentId: 'payment-demo-3',
      amount: 5000,
      installerName: 'Test User',
      fromInstaller: true
    },
    read: false,
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString() // 2 hours ago
  },
  {
    id: 'admin-notif-3',
    recipientId: 'admin',
    recipientType: 'admin',
    type: 'serial_submission',
    title: 'New Serial Number Submitted',
    message: 'John Doe submitted serial number SX987654321',
    data: {
      serialNumber: 'SX987654321',
      installerName: 'John Doe',
      inverterModel: 'SunX Pro 5kW',
      fromInstaller: true
    },
    read: false,
    createdAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString() // 4 hours ago
  },
  {
    id: 'admin-notif-4',
    recipientId: 'admin',
    recipientType: 'admin',
    type: 'new_installer',
    title: 'New Installer Registration',
    message: 'Ahmad Ali registered as a new installer',
    data: {
      installerId: 'installer-3',
      installerName: 'Ahmad Ali',
      city: 'Lahore',
      fromInstaller: true
    },
    read: true,
    createdAt: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString() // 6 hours ago
  },
  {
    id: 'admin-notif-5',
    recipientId: 'admin',
    recipientType: 'admin',
    type: 'payment_comment',
    title: 'New Payment Comment',
    message: 'Test User added a comment: "I have uploaded the payment receipt. Please check."',
    data: {
      paymentId: 'payment-demo-1',
      installerName: 'Test User',
      comment: 'I have uploaded the payment receipt. Please check.',
      fromInstaller: true
    },
    read: true,
    createdAt: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString() // 8 hours ago
  }
];

// Persistent storage helper
const persistentStorage = {
  save: (key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save to localStorage:', error);
    }
  },

  load: (key, defaultValue = []) => {
    try {
      const stored = localStorage.getItem(key);
      return stored ? JSON.parse(stored) : defaultValue;
    } catch (error) {
      console.warn('Failed to load from localStorage:', error);
      return defaultValue;
    }
  }
};

class AdminNotificationService {
  constructor() {
    this.eventListeners = new Map();
    this.storageKey = 'sunx_admin_notifications';
    this.pollingInterval = null;
    this.lastNotificationCount = 0;
    this.socketConnected = false;
    this.initializeNotifications();

    // Only initialize socket and polling if admin is authenticated
    if (this.isAdminAuthenticated()) {
      this.initializeSocket();
      this.startPolling(); // Keep polling as fallback
    }
  }

  // Check if admin is authenticated
  isAdminAuthenticated() {
    const adminToken = localStorage.getItem('adminToken');
    const userType = localStorage.getItem('userType');
    return adminToken && adminToken !== 'null' && adminToken !== 'undefined' && userType === 'admin';
  }

  initializeNotifications() {
    // Load notifications from localStorage or use initial data
    this.notifications = persistentStorage.load(this.storageKey, initialAdminNotifications);
  }

  // Initialize Socket.IO connection
  initializeSocket() {
    console.log('🔌 Initializing Socket.IO for admin notifications...');

    // Connect to Socket.IO server
    socketService.connect('admin');

    // Listen for socket events
    socketService.on('socket_connected', () => {
      console.log('✅ Admin notification socket connected');
      this.socketConnected = true;
    });

    socketService.on('socket_authenticated', (data) => {
      console.log('✅ Admin notification socket authenticated:', data);
    });

    socketService.on('new_notification', (notification) => {
      console.log('📢 Real-time notification received:', notification);
      this.handleRealtimeNotification(notification);
    });

    socketService.on('system_message', (message) => {
      console.log('📢 System message received:', message);
      this.handleRealtimeNotification(message);
    });

    socketService.on('socket_disconnected', () => {
      console.log('🔌 Admin notification socket disconnected');
      this.socketConnected = false;
    });

    socketService.on('socket_error', (error) => {
      console.error('❌ Admin notification socket error:', error);
      this.socketConnected = false;
    });
  }

  // Handle real-time notifications from Socket.IO
  handleRealtimeNotification(notification) {
    // Add to local notifications
    this.notifications.unshift(notification);
    this.saveToStorage();

    // Emit event for UI components
    this.emit('notification_added', notification);

    console.log('📢 Real-time notification processed:', notification.title);
  }

  // Start polling for new notifications
  startPolling() {
    // Only start polling if admin is authenticated
    if (!this.isAdminAuthenticated()) {
      console.log('🔍 Admin not authenticated, skipping notification polling');
      return;
    }

    // Poll every 10 seconds for new notifications
    this.pollingInterval = setInterval(async () => {
      try {
        // Check authentication before each poll
        if (this.isAdminAuthenticated()) {
          await this.checkForNewNotifications();
        } else {
          console.log('🔍 Admin authentication lost, stopping notification polling');
          this.stopPolling();
        }
      } catch (error) {
        console.error('Error polling for notifications:', error);
      }
    }, 10000); // 10 seconds
  }

  // Stop polling
  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  // Check for new notifications from backend
  async checkForNewNotifications() {
    try {
      // Double-check authentication before making API call
      if (!this.isAdminAuthenticated()) {
        console.log('🔍 Admin not authenticated, skipping notification check');
        return;
      }

      const response = await fetch('/api/admin/notifications?limit=50', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const backendNotifications = data.data.notifications;
          const currentCount = backendNotifications.length;

          // Check if there are new notifications
          if (currentCount > this.lastNotificationCount) {
            const newNotifications = backendNotifications.slice(0, currentCount - this.lastNotificationCount);

            // Emit events for new notifications
            newNotifications.forEach(notif => {
              const formattedNotif = {
                id: notif._id,
                type: notif.type,
                title: notif.title,
                message: notif.message,
                read: notif.read,
                createdAt: notif.createdAt,
                data: notif.data,
                priority: notif.priority,
                fromBackend: true
              };

              this.emit('notification_added', formattedNotif);
            });
          }

          this.lastNotificationCount = currentCount;
        }
      }
    } catch (error) {
      console.error('Error checking for new notifications:', error);
    }
  }

  // Save notifications to localStorage
  saveToStorage() {
    persistentStorage.save(this.storageKey, this.notifications);
  }

  // Get all notifications for admin
  async getNotifications() {
    try {
      // Check if admin is authenticated
      const adminToken = localStorage.getItem('adminToken');
      if (!adminToken || adminToken === 'null' || adminToken === 'undefined') {
        console.log('🔍 No admin token found, using local notifications only');
        return this.notifications.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      }

      // Fetch from backend API first
      const response = await fetch('/api/admin/notifications', {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Merge backend notifications with local ones
          const backendNotifications = data.data.notifications.map(notif => ({
            id: notif._id,
            type: notif.type,
            title: notif.title,
            message: notif.message,
            read: notif.read,
            createdAt: notif.createdAt,
            data: notif.data,
            priority: notif.priority,
            fromBackend: true
          }));

          // Combine with local notifications (for backward compatibility)
          const localNotifications = this.notifications.filter(n => !n.fromBackend);
          const allNotifications = [...backendNotifications, ...localNotifications];

          // Update local storage with merged notifications
          this.notifications = allNotifications;
          this.saveToStorage();

          return allNotifications.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        }
      }

      // Fallback to local notifications if API fails
      return this.notifications.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } catch (error) {
      console.error('Error fetching admin notifications:', error);
      // Fallback to local notifications
      return this.notifications.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }
  }

  // Add new notification
  addNotification(notification) {
    const newNotification = {
      id: `admin-notif-${Date.now()}`,
      recipientId: 'admin',
      recipientType: 'admin',
      read: false,
      createdAt: new Date().toISOString(),
      ...notification
    };

    this.notifications.unshift(newNotification);
    this.saveToStorage(); // Save to localStorage
    this.emit('notification_added', newNotification);

    return newNotification;
  }

  // Mark notification as read
  async markAsRead(notificationId) {
    try {
      const notification = this.notifications.find(n => n.id === notificationId);
      if (notification) {
        // If it's a backend notification, update via API
        if (notification.fromBackend) {
          const response = await fetch(`/api/admin/notifications/${notificationId}/read`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            notification.read = true;
            this.saveToStorage();
            this.emit('notification_read', notification);
          }
        } else {
          // Local notification
          notification.read = true;
          this.saveToStorage();
          this.emit('notification_read', notification);
        }
      }
      return true;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  // Mark all notifications as read
  async markAllAsRead() {
    try {
      // Update backend notifications via API
      const response = await fetch('/api/admin/notifications/read-all', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        // Mark all local notifications as read too
        this.notifications.forEach(notification => {
          notification.read = true;
        });
        this.saveToStorage();
        this.emit('all_notifications_read');
      }
      return true;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      // Fallback to local only
      this.notifications.forEach(notification => {
        notification.read = true;
      });
      this.saveToStorage();
      this.emit('all_notifications_read');
      throw error;
    }
  }

  // Get unread count
  async getUnreadCount() {
    try {
      // Get fresh data from backend
      await this.getNotifications();
      return this.notifications.filter(n => !n.read).length;
    } catch (error) {
      console.error('Error getting unread count:', error);
      return this.notifications.filter(n => !n.read).length;
    }
  }

  // Cleanup method
  destroy() {
    this.stopPolling();
    this.eventListeners.clear();
  }

  // Add chat message notification (only for installer messages)
  addChatNotification(message) {
    // STRICT CHECK: Only create notifications for messages FROM installers TO admin
    // Prevent any admin self-notifications
    if (message.senderType === 'installer' &&
        message.senderName &&
        !message.senderName.toLowerCase().includes('admin') &&
        !message.senderName.toLowerCase().includes('support')) {

      console.log('Creating chat notification for installer message:', {
        senderType: message.senderType,
        senderName: message.senderName,
        message: message.message
      });

      this.addNotification({
        type: 'new_message',
        title: `New Message from ${message.senderName}`,
        message: message.message.length > 50 ? message.message.substring(0, 50) + '...' : message.message,
        data: {
          senderId: message.senderId,
          senderName: message.senderName,
          chatId: message.chatId,
          fromInstaller: true
        }
      });
    } else {
      console.log('Skipping notification for non-installer message:', {
        senderType: message.senderType,
        senderName: message.senderName
      });
    }
  }

  // Add payment request notification
  addPaymentRequestNotification(paymentData) {
    this.addNotification({
      type: 'payment_request',
      title: 'New Payment Request',
      message: `${paymentData.installerName} submitted a payment request for PKR ${paymentData.amount.toLocaleString()}`,
      data: {
        paymentId: paymentData.paymentId,
        amount: paymentData.amount,
        installerName: paymentData.installerName,
        fromInstaller: true
      }
    });
  }

  // Add payment comment notification
  addPaymentCommentNotification(commentData) {
    // Only create notifications for installer comments, not admin comments
    if (commentData.userType === 'installer') {
      this.addNotification({
        type: 'payment_comment',
        title: 'New Payment Comment',
        message: `${commentData.userName} added a comment: "${commentData.comment.length > 50 ? commentData.comment.substring(0, 50) + '...' : commentData.comment}"`,
        data: {
          paymentId: commentData.paymentId,
          installerName: commentData.userName,
          comment: commentData.comment,
          fromInstaller: true
        }
      });
    }
  }

  // Add serial submission notification
  addSerialSubmissionNotification(serialData) {
    this.addNotification({
      type: 'serial_submission',
      title: 'New Serial Number Submitted',
      message: `${serialData.installerName} submitted serial number ${serialData.serialNumber}`,
      data: {
        serialNumber: serialData.serialNumber,
        installerName: serialData.installerName,
        inverterModel: serialData.inverterModel,
        fromInstaller: true
      }
    });
  }

  // Event handling
  on(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.eventListeners.has(event)) {
      const listeners = this.eventListeners.get(event);
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  emit(event, data) {
    if (this.eventListeners.has(event)) {
      this.eventListeners.get(event).forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error('Error in event listener:', error);
        }
      });
    }
  }

  // Reset notifications to initial state (for testing/debugging)
  resetNotifications() {
    this.notifications = [...initialAdminNotifications];
    this.saveToStorage();
    console.log('🔄 Admin notifications reset to initial state');
  }

  // Clear all notifications
  clearAllNotifications() {
    this.notifications = [];
    this.saveToStorage();
    console.log('🗑️ All admin notifications cleared');
  }

  // Cleanup
  cleanup() {
    this.eventListeners.clear();
  }
}

export const adminNotificationService = new AdminNotificationService();
